import { Platform } from "react-native";
import { palette } from "./tokens/palette";

/**
 * Global "retrofit" dark-mode color substitution — WEB ONLY.
 *
 * WHY THIS EXISTS
 * ----------------
 * Individually migrating every screen/component to `useTheme()` is a large,
 * slow effort. This module is a *centralized, global* stop-gap layer that
 * instantly re-colors the common "surface" colors (page/card backgrounds,
 * primary/secondary text, borders/dividers, and brand-blue shades) used
 * throughout the app's *existing* `StyleSheet.create()` calls — WITHOUT
 * editing those component files — whenever Dark mode is active.
 *
 * HOW IT WORKS (react-native-web specific)
 * -----------------------------------------
 * react-native-web's `StyleSheet.create()` compiles every unique
 * property+color declaration into a single, deduplicated, atomic CSS rule
 * and inserts it into one shared live stylesheet:
 * `<style id="react-native-stylesheet">` (a real CSSOM `CSSStyleSheet`,
 * confirmed via `createCSSStyleSheet.js`/`createOrderedCSSStyleSheet.js`).
 * Because RNW dedupes identical property+value pairs into ONE shared
 * class/rule, directly mutating that single CSSOM rule via
 * `rule.style.cssText = ...` instantly re-colors *every* component in the
 * entire app using that literal declaration — with zero React re-renders
 * and zero per-component code changes.
 *
 * PROPERTY-SCOPE-AWARE MATCHING (critical correctness fix)
 * -----------------------------------------------------------
 * An earlier version of this module matched raw color VALUES anywhere in
 * a rule's `cssText`, regardless of which CSS property they belonged to.
 * That is unsafe: the same literal color (e.g. white/`#ffffff`) is
 * frequently used both as a `backgroundColor` (which SHOULD invert to a
 * dark surface) and as a `color`/text value (e.g. white button/overlay
 * text sitting on a colored surface that is NOT swept, which must STAY
 * white or it becomes invisible). Value-only matching would have
 * incorrectly darkened that legitimate white text too.
 *
 * This module now parses each rule's declarations individually, resolves
 * which semantic *scope* the property belongs to (`bg`, `border`, or
 * `text`), and only looks up/replaces the color within the curated map
 * for THAT scope. A color is only ever transformed when it is being used
 * in a role we recognize (background/border/text) and only using the
 * dark target appropriate for that specific role.
 *
 * CURATED MAP + GENERIC FALLBACK
 * --------------------------------
 * A hand-curated map (built from data-driven repo-wide color-frequency
 * analysis) handles the known, high-confidence cases precisely. For any
 * OTHER neutral (low-saturation, i.e. gray/white/black-ish, not a
 * colorful brand/status hue) color not explicitly curated, a generic
 * luminance-bucket fallback computes a reasonable dark equivalent based
 * on the color's lightness and the property scope it's used in. This
 * means new/uncatalogued neutral grays introduced later are still
 * handled automatically, rather than silently falling through as an
 * unfixed white background or unreadable text — directly closing the
 * "whack-a-mole" gap that caused repeated bug reports.
 *
 * Colorful/saturated values (status reds/greens/ambers, brand accent
 * hues, etc.) are intentionally left untouched by the generic fallback
 * (see `NEUTRAL_SATURATION_THRESHOLD`) so status/semantic meaning is
 * never accidentally recolored.
 *
 * WHAT THIS DOES NOT COVER
 * -------------------------
 * - Native (iOS/Android): there is no CSSOM/atomic-class mechanism on
 *   native RN, so this module is a guarded no-op there. Native dark mode
 *   coverage is limited to components already migrated to `useTheme()`.
 * - Inline style literals passed directly as `style={{ color: '#fff' }}`
 *   (not via `StyleSheet.create()`) are NOT compiled into the shared
 *   stylesheet, so they are unaffected by this sweep.
 * - Any *saturated* color literal not in the curated map (status/tag/
 *   brand-illustration colors) is left untouched by design.
 * - The outer page chrome (`<html>`, `<body>`, and the web root mount
 *   node) sits outside react-native-web's component tree entirely, so it
 *   has no StyleSheet.create() rule to sweep; `applyRootBackground()`
 *   below handles this separately via a direct inline style.
 * - Lazily-loaded (`React.lazy`) screen chunks insert their
 *   `StyleSheet.create()` rules into the shared sheet only once first
 *   imported. To avoid any visible "flash" of un-swept Light colors while
 *   Dark mode is active, this module patches `CSSStyleSheet.prototype.
 *   insertRule` ONCE (see `patchInsertRuleForInstantSweep`) so that any
 *   newly-inserted rule into RNW's shared sheet is swept synchronously,
 *   immediately after insertion — no polling/interval delay. A long-
 *   interval safety net remains in `ThemeContext.tsx` purely as a
 *   defensive backstop, not as the primary mechanism.
 */

const STYLESHEET_ID = "react-native-stylesheet";

// ---------------------------------------------------------------------------
// Dark theme target tokens — sourced directly from the palette (the single
// source of truth also consumed by src/theme/tokens/darkTheme.ts) so this
// module never invents or duplicates its own color decisions. Any future
// refinement of the dark palette automatically flows through here too.
// ---------------------------------------------------------------------------
const DARK_SURFACE = palette.darkSurface;
const DARK_ALMOST_BLACK = palette.almostBlack;
const DARK_ELEVATED = palette.darkElevated;
const DARK_TEXT_PRIMARY = palette.darkTextPrimary;
const DARK_TEXT_SECONDARY = palette.darkTextSecondary;
const DARK_TEXT_DISABLED = palette.darkTextDisabled;
const DARK_BORDER = palette.darkBorder;
const DARK_DIVIDER = palette.darkDivider;
const DARK_INPUT_BORDER = palette.darkInputBorder;
const DARK_BRAND_BLUE = palette.darkBrandBlue;

export type Scope = "bg" | "border" | "text";

const BG_PROPS = new Set(["background-color", "background"]);
const BORDER_PROPS = new Set([
  "border-color",
  "border-top-color",
  "border-bottom-color",
  "border-left-color",
  "border-right-color",
  "outline-color",
]);
const TEXT_PROPS = new Set(["color"]);

const scopeForProp = (prop: string): Scope | null => {
  if (BG_PROPS.has(prop)) return "bg";
  if (BORDER_PROPS.has(prop)) return "border";
  if (TEXT_PROPS.has(prop)) return "text";
  return null;
};

// ---------------------------------------------------------------------------
// Color parsing — unifies hex / rgb()/rgba() / the handful of CSS keyword
// colors actually used in this codebase (`white`, `black`) into one [r,g,b]
// triplet, so curated map lookups work identically no matter which literal
// form the source code (or the browser's own CSSOM normalization) used.
// ---------------------------------------------------------------------------
const NAMED_COLORS: Record<string, [number, number, number]> = {
  white: [255, 255, 255],
  black: [0, 0, 0],
};

export const parseRgb = (value: string): [number, number, number] | null => {
  const v = value.trim().toLowerCase();
  if (NAMED_COLORS[v]) return NAMED_COLORS[v];

  const hexMatch = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch) {
    const hex = hexMatch[1];
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex;
    return [
      parseInt(full.substring(0, 2), 16),
      parseInt(full.substring(2, 4), 16),
      parseInt(full.substring(4, 6), 16),
    ];
  }

  const rgbMatch = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }

  return null;
};

const rgbKey = (rgb: [number, number, number]): string => rgb.join(",");

const buildLookup = (entries: Record<string, string>): Map<string, string> => {
  const map = new Map<string, string>();
  Object.entries(entries).forEach(([from, to]) => {
    const rgb = parseRgb(from);
    if (rgb) map.set(rgbKey(rgb), to);
  });
  return map;
};

// ---------------------------------------------------------------------------
// Curated Light -> Dark source colors, data-driven from repo-wide color-
// frequency analysis, grouped by the semantic role they play (scope).
// A color's key is its PARSED RGB TRIPLET, so `white` and `#ffffff` (and
// `rgb(255, 255, 255)`) are automatically treated as the same source —
// no separate keyword entries are needed.
// ---------------------------------------------------------------------------

// Brand blue: applies regardless of scope (button backgrounds, link/heading
// text, active-tab/underline borders all use one of these near-duplicate
// light blues and should all collapse onto the single dark brand blue).
const BRAND_LOOKUP = buildLookup({
  "#005cbc": DARK_BRAND_BLUE,
  "#034895": DARK_BRAND_BLUE,
  "#0066cc": DARK_BRAND_BLUE,
  "#004895": DARK_BRAND_BLUE,
  "#0b5fc6": DARK_BRAND_BLUE,
  "#3582e6": DARK_BRAND_BLUE,
  "#005ec0": DARK_BRAND_BLUE,
  "#004b93": DARK_BRAND_BLUE, // DLO chrome navy
  "#002366": DARK_BRAND_BLUE,
  "#316bb8": DARK_BRAND_BLUE,
  "#006fce": DARK_BRAND_BLUE,
});

const BG_LOOKUP = buildLookup({
  // Near-white/light-neutral card backgrounds -> darkSurface
  "#ffffff": DARK_SURFACE,
  "#fff": DARK_SURFACE,
  "#fbfcfe": DARK_SURFACE,
  "#f0f0f0": DARK_SURFACE,

  // App/page-level near-white -> almostBlack
  "#f8f8f8": DARK_ALMOST_BLACK,
  "#fafafa": DARK_ALMOST_BLACK,

  // Reclassified: confirmed via usage-context grep to be dominantly used as
  // the OUTER PAGE container background (not cards) on module landing pages
  // (Shift Handover, New Hire Onboarding, Quality Audit Generator, Admin/
  // Configuration, Employee Updates, Key Focus Areas, Roles, Perfect Truck,
  // Ticketing Portal). Previously bucketed as darkSurface/darkElevated, which
  // put the page background at the same or a LIGHTER level than the actual
  // card backgrounds, eliminating visual separation. Moved to almostBlack so
  // the page reads clearly darker than its cards.
  "#f8f9fa": DARK_ALMOST_BLACK, // Shift Handover / Quality Audit Generator page bg (rgb(248,249,250))
  "#f5f5f5": DARK_ALMOST_BLACK, // New Hire Onboarding page bg
  "#f5f9ff": DARK_ALMOST_BLACK, // AdminColors.bgPage — Employee Updates / Perfect Truck / Ticketing Portal etc.
  "#eef2f8": DARK_ALMOST_BLACK, // Configuration Module / Roles / Key Focus Areas page bg
  "#f0f5fc": DARK_ALMOST_BLACK, // Labor Optimization (DLO) page-level section backgrounds (rgb(240,245,252))
  "#f5f7fa": DARK_ALMOST_BLACK, // ShiftHandoverRedirectScreen page/form background

  // Light blue/gray-tinted elevated panels, icon-circle tints, active-state
  // highlights -> darkElevated (confirmed dominant usage is small accent/
  // highlight tints, not page containers — left unchanged)
  "#f3f7fd": DARK_ELEVATED,
  "#eff5fc": DARK_ELEVATED,
  "#e9ecef": DARK_ELEVATED,
  "#daeefa": DARK_ELEVATED,
  "#e8f0fb": DARK_ELEVATED, // confirmed background usage (icon circles, active tints)
  "#eff6ff": DARK_ELEVATED,
  "#eef5ff": DARK_ELEVATED,
  "#efefef": DARK_ELEVATED,
  "#f3f4f6": DARK_ELEVATED,
  "#f2f2f2": DARK_ELEVATED, // light bg literal — distinct from darkTextPrimary target since scope differs
  "#d6e8fc": DARK_ELEVATED, // LHOptionCard selected-state highlight (saturation exceeds the
  // generic neutral fallback threshold, so it must be curated explicitly — without this
  // entry the selected checkbox background stays light-blue while its text is swept to
  // near-white, making the selected state unreadable ("becomes white") in Dark mode.

  // Pale status/tag badge + banner fills. These are saturated, so the generic
  // fallback leaves them untouched, while all text is forced to white in Dark
  // mode -> white-on-pale (unreadable). Map each to a solid dark tone of the same
  // hue so the (white) label stays readable and the status meaning is preserved.
  "#caf3c6": "#1A6B24", // success / green badge (AdminColors.green75)
  "#ffe5de": "#9B2626", // error / red badge (AdminColors.red75)
  "#fef1b9": "#92600A", // warning / amber badge + banner (AdminColors.yellow50)
  "#f9e0ca": "#8A4B12", // orange tag
  "#fcecde": "#8A4B12", // orange tag (AdminColors.tagOrange)
  "#e1e3fd": "#4A3FA0", // purple tag
  "#aad2e2": "#1E5A7A", // teal tag
  // Ticketing Portal urgency / status pills
  "#ffe6e4": "#9B2626", // urgency: critical
  "#ffe9d1": "#8A4B12", // urgency: high, status: waiting on user / submitted to ADO
  "#fff6cc": "#92600A", // urgency: medium, status: waiting on IT / TMV
  "#fff3e0": "#8A4B12", // warning/error card fill (Crewing Analytics section errors, HomepageConfig, etc.) — saturation range (31) narrowly exceeds NEUTRAL_SATURATION_THRESHOLD
  "#fff4d9": "#92600A", // access request: modify
  "#edeafb": "#4A3FA0", // access request: temporary
  "#fef2f2": "#9B2626", // access request: denied
  "#fff7ed": "#8A4B12", // access request: modification requested
  "#f0fdf4": "#1A6B24", // access request: approved
  "#ddf9da": "#1A6B24", // active / non-union green badge
  "#fddede": "#9B2626", // red tag (RoleTable)
  "#d5f5dc": "#1A6B24", // "Same access at all selected sites" badge (User Management Add/Detail) — saturation range (32) narrowly exceeds NEUTRAL_SATURATION_THRESHOLD
  "#dcfce7": "#1A6B24", // full-access success banner (AccessRequestLocationPicker)
  "#fef3c7": "#92600A", // amber count badge (AccessRequestLocationPicker)

  // Chart grid lines drawn as 1px filled <View>s (DLO bar charts). #dfdfdf is
  // only curated as a border colour; used as a background it fell through to
  // the generic fallback (-> darkSurface), identical to the card, so the grid
  // vanished. Map it to the visible border tone.
  "#dfdfdf": DARK_BORDER,

  // Disabled primary button fill (e.g. Apply with no pending changes). The
  // generic fallback would turn this into DARK_SURFACE, identical to the filter
  // panel background, making the button vanish. Keep a visible, muted fill.
  "#d0d5dd": "#3A3A3A",

  // Medium-gray disabled/muted button backgrounds (e.g. NHO Complete button disabled state) ->
  // darkSurface so white text keeps sufficient contrast (≥7:1) and does not appear light-gray.
  "#999": DARK_SURFACE,
  "#999999": DARK_SURFACE,
  "#cccccc": DARK_SURFACE,
});

const BORDER_LOOKUP = buildLookup({
  "#d0d0d0": DARK_BORDER,
  "#c0c0c0": DARK_BORDER,
  "#ccc": DARK_BORDER,
  // Reclassified: these are predominantly TABLE/GRID border colors
  // (AdminColors.borderTable etc. — confirmed via usage-context grep:
  // 100+ borderColor usages vs ~20 background usages), NOT backgrounds.
  // Previously bucketed under elevated-background, which made table
  // borders blend invisibly into similarly-dark surfaces.
  "#d7e5f8": DARK_BORDER,
  "#d8e7fb": DARK_BORDER,
  "#ddd": DARK_BORDER,

  "#e0e0e0": DARK_DIVIDER,
  "#e6e6e6": DARK_DIVIDER,
  "#ebebeb": DARK_DIVIDER,
  "#dfdfdf": DARK_DIVIDER,
  "#dee2e6": DARK_DIVIDER,
  "#e5e7eb": DARK_DIVIDER,
  "#d1d5db": DARK_DIVIDER,
  "#eee": DARK_DIVIDER,

  "#dadada": DARK_INPUT_BORDER,
  "#ced4da": DARK_INPUT_BORDER,
});

const TEXT_LOOKUP = buildLookup({
  // Near-black text -> darkTextPrimary
  "#2d2d2d": DARK_TEXT_PRIMARY,
  "#3a3a3a": DARK_TEXT_PRIMARY,
  "#000": DARK_TEXT_PRIMARY,
  "#000000": DARK_TEXT_PRIMARY, // also covers the `black` keyword (same rgb key)
  "#333": DARK_TEXT_PRIMARY,
  "#212529": DARK_TEXT_PRIMARY,
  "#111827": DARK_TEXT_PRIMARY,
  "#1c1c1c": DARK_TEXT_PRIMARY,
  "#343a40": DARK_TEXT_PRIMARY,
  "#1f2937": DARK_TEXT_PRIMARY,
  "#1a2332": DARK_TEXT_PRIMARY,
  "#24324a": DARK_TEXT_PRIMARY, // Ticketing Portal hero greeting/user name — saturation range (38) narrowly
  // exceeds NEUTRAL_SATURATION_THRESHOLD (30), so the generic fallback skips it; curated explicitly.

  // Shift Handover: table title / confirmation popup text / pagination text —
  // these must read near-white in Dark mode rather than a muted gray, so they
  // are curated to darkTextPrimary explicitly (not left to the secondary/
  // disabled buckets used by other generic mid-grays below).
  "#495057": DARK_TEXT_PRIMARY,
  "#6c757d": DARK_TEXT_PRIMARY,

  // Mid grays (secondary/muted text) -> darkTextSecondary
  "#616161": DARK_TEXT_SECONDARY,
  "#556377": DARK_TEXT_SECONDARY, // Ticketing Portal hero subtitle — saturation range (34) narrowly exceeds
  // NEUTRAL_SATURATION_THRESHOLD (30); curated explicitly for the same reason as #24324a above.
  "#818181": DARK_TEXT_SECONDARY,
  "#838383": DARK_TEXT_SECONDARY,
  "#4b4b4b": DARK_TEXT_SECONDARY,
  "#585858": DARK_TEXT_SECONDARY,
  "#666": DARK_TEXT_SECONDARY,
  "#666666": DARK_TEXT_SECONDARY,
  "#6b7280": DARK_TEXT_SECONDARY,
  "#374151": DARK_TEXT_SECONDARY,
  "#656565": DARK_TEXT_SECONDARY,
  "#9099aa": DARK_TEXT_SECONDARY,
  "#6b6b6b": DARK_TEXT_SECONDARY,
  "#a8a8a8": DARK_TEXT_SECONDARY,
  "#3d4147": DARK_TEXT_SECONDARY,
  "#868e96": DARK_TEXT_SECONDARY,
  "#757575": DARK_TEXT_SECONDARY,
  "#9aa4b2": DARK_TEXT_SECONDARY,
  "#555": DARK_TEXT_SECONDARY,

  // Lighter grays (disabled/placeholder) -> darkTextDisabled
  "#999": DARK_TEXT_DISABLED,
  "#999999": DARK_TEXT_DISABLED,
  "#a0a0a0": DARK_TEXT_DISABLED,
  "#b1b1b1": DARK_TEXT_DISABLED,
  "#9ca3af": DARK_TEXT_DISABLED,
  "#cccccc": DARK_TEXT_DISABLED,
  "#adb5bd": DARK_TEXT_DISABLED,
  "#9e9e9e": DARK_TEXT_DISABLED,
  "#b0b0b0": DARK_TEXT_DISABLED, // light-mode text literal — distinct from darkTextSecondary target since scope differs
  // Warning banner text (OneWay/RouteRoadblocks) — dark brown on light yellow
  // must become light on dark brown in dark mode
  "#6b4a05": DARK_TEXT_PRIMARY,});

// ---------------------------------------------------------------------------
// Generic fallback for neutral (low-saturation) colors NOT in the curated
// maps above. Keeps status/tag/brand-illustration colors (saturated hues)
// untouched, while still fixing any un-catalogued gray/white/black-ish
// literal so it doesn't silently remain a white background or unreadable
// text forever.
// ---------------------------------------------------------------------------
const NEUTRAL_SATURATION_THRESHOLD = 30;

const luminance = (r: number, g: number, b: number): number =>
  0.2126 * r + 0.7152 * g + 0.0722 * b;

const saturationRange = (r: number, g: number, b: number): number =>
  Math.max(r, g, b) - Math.min(r, g, b);

const genericFallback = (
  scope: Scope,
  rgb: [number, number, number]
): string | null => {
  const [r, g, b] = rgb;
  if (saturationRange(r, g, b) > NEUTRAL_SATURATION_THRESHOLD) return null;

  const lum = luminance(r, g, b);

  if (scope === "bg") {
    if (lum >= 190) return DARK_SURFACE;
    if (lum >= 140) return DARK_ELEVATED;
    if (lum >= 90) return DARK_BORDER;
    return null; // already dark enough — leave unchanged
  }

  if (scope === "border") {
    if (lum >= 130) return DARK_INPUT_BORDER;
    if (lum >= 40) return DARK_BORDER;
    return null;
  }

  // text
  if (lum <= 90) return DARK_TEXT_PRIMARY;
  if (lum <= 170) return DARK_TEXT_SECONDARY;
  if (lum <= 210) return DARK_TEXT_DISABLED;
  return null; // already light text (e.g. white-on-colored-surface) — leave unchanged
};

// Semantic text colors that must survive Dark mode untouched (already tuned for
// dark surfaces by the component that sets them), e.g. the overdue red / amber /
// green dates in Employee Updates. Without this the text-scope rule below would
// rewrite them to white and the status meaning would be lost.
const KEEP_TEXT_LOOKUP = buildLookup({
  "#ff6b6b": "#FF6B6B", // overdue (red)
  "#ffb020": "#FFB020", // due soon (amber)
  "#4ade80": "#4ADE80", // on time (green)
});

export const resolveTarget = (
  scope: Scope,
  rgb: [number, number, number]
): string | null => {
  const key = rgbKey(rgb);

  // Brand-blue shades must be checked FIRST (across ALL scopes, including text)
  // so that e.g. #005CBC used as a link/heading text color becomes the dark
  // brand blue (#3D8BFF) instead of being swept to white by the generic
  // text→white fallback below.
  const brand = BRAND_LOOKUP.get(key);
  if (brand) return brand;

  if (scope === "text") {
    if (KEEP_TEXT_LOOKUP.has(key)) return null; // leave the component's color as-is
    return "#FFFFFF";
  }

  const scopedLookup =
    scope === "bg" ? BG_LOOKUP : scope === "border" ? BORDER_LOOKUP : TEXT_LOOKUP;
  const curated = scopedLookup.get(key);
  if (curated) return curated;

  return genericFallback(scope, rgb);
};

const isWeb = () => Platform.OS === "web" && typeof document !== "undefined";

/**
 * Rewrites a rule's pristine (Light) `cssText` for Dark mode, one
 * declaration at a time, only transforming declarations whose property is
 * a recognized bg/border/text scope AND whose color resolves to a dark
 * target (curated or generic-fallback). All other declarations (and
 * unrecognized/unparseable colors) are passed through unchanged.
 */
const rewriteDeclarationsForDark = (cssText: string): string => {
  const parts = cssText
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return cssText;

  let changed = false;
  const rewritten = parts.map((token) => {
    const idx = token.indexOf(":");
    if (idx === -1) return token;

    const prop = token.slice(0, idx).trim().toLowerCase();
    const rawValue = token.slice(idx + 1).trim();

    const scope = scopeForProp(prop);
    if (!scope) return token;

    const rgb = parseRgb(rawValue);
    if (!rgb) return token;

    const target = resolveTarget(scope, rgb);
    if (!target) return token;

    const targetRgb = parseRgb(target);
    if (!targetRgb) return token;

    changed = true;
    return `${prop}: rgb(${targetRgb[0]}, ${targetRgb[1]}, ${targetRgb[2]})`;
  });

  return changed ? `${rewritten.join("; ")};` : cssText;
};

// Captures each rule's original (Light) cssText the first time we ever see
// it, so re-applying Light mode (or recomputing Dark mode) is always
// derived from the pristine value rather than a previously-mutated one.
const pristineCssTextByRule = new WeakMap<CSSStyleRule, string>();

/**
 * Sweeps react-native-web's shared stylesheet, swapping curated/derived
 * Light colors for their Dark equivalents (or restoring the pristine Light
 * value). Idempotent and safe to call repeatedly/on an interval/from the
 * instant insertRule hook.
 */
const sweepStylesheet = (resolvedTheme: "light" | "dark"): void => {
  const styleEl = document.getElementById(
    STYLESHEET_ID
  ) as HTMLStyleElement | null;
  const sheet = styleEl?.sheet as CSSStyleSheet | undefined;
  if (!sheet) return;

  let rules: CSSRuleList;
  try {
    rules = sheet.cssRules;
  } catch {
    return; // Not accessible yet (e.g. cross-origin edge case) — bail safely.
  }

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i] as CSSStyleRule;
    if (!rule || !rule.style) continue;

    if (!pristineCssTextByRule.has(rule)) {
      pristineCssTextByRule.set(rule, rule.style.cssText);
    }
    const pristine = pristineCssTextByRule.get(rule) as string;

    if (resolvedTheme === "light") {
      if (rule.style.cssText !== pristine) {
        rule.style.cssText = pristine;
      }
      continue;
    }

    const updated = rewriteDeclarationsForDark(pristine);
    if (rule.style.cssText !== updated) {
      rule.style.cssText = updated;
    }
  }
};

/**
 * Handles the outer page chrome (<html>, <body>, and the web root mount
 * node) which sits OUTSIDE react-native-web's component tree entirely and
 * therefore has no StyleSheet.create() rule to sweep. Without this, these
 * elements keep the browser's default white background, which shows
 * through as light-colored edges/overscroll/gaps even once every
 * component-level color has been swapped. Only ever sets an explicit
 * inline override in Dark mode; clears it (empty string) otherwise so
 * Light mode is provably unchanged.
 */
const applyRootBackground = (resolvedTheme: "light" | "dark"): void => {
  const color = resolvedTheme === "dark" ? DARK_ALMOST_BLACK : "";
  document.documentElement.style.backgroundColor = color;
  if (document.body) {
    document.body.style.backgroundColor = color;
  }
  const rootEl = document.getElementById("root");
  if (rootEl) {
    (rootEl as HTMLElement).style.backgroundColor = color;
  }
};

const ROOT_TEXT_STYLE_ID = "global-dark-mode-text-overrides";

const applyRootTextDefaults = (resolvedTheme: "light" | "dark"): void => {
  const existing = document.getElementById(ROOT_TEXT_STYLE_ID);

  if (resolvedTheme === "light") {
    existing?.remove();
    return;
  }

  const style = existing || document.createElement("style");
  style.id = ROOT_TEXT_STYLE_ID;
  style.textContent = `
    /* Elements carrying data-lh-keep-color (set via dataSet={{ lhKeepColor: "1" }})
       opt out of the blanket white text so semantic text colors (e.g. overdue
       red / amber / green dates) survive in Dark mode. */
    html, body, #root, #root *:not([data-lh-keep-color]) {
      color: #FFFFFF !important;
      -webkit-text-fill-color: #FFFFFF !important;
    }
    /* -webkit-text-fill-color is inherited and paints over color, so opted-out
       elements must reset it to their own color or they still render white.
       Not scoped to #root: react-native-web Modals/overlays are portaled to
       document.body, so they inherit the white fill from <body> instead. */
    [data-lh-keep-color] {
      -webkit-text-fill-color: currentColor !important;
    }
    /* Disabled controls (react-native-web sets aria-disabled="true" on disabled
       Touchables/Pressables): dim the label so it reads as inactive instead of
       the same bright white as an enabled button. */
    #root [aria-disabled="true"] *:not([data-lh-keep-color]) {
      color: #8C8C8C !important;
      -webkit-text-fill-color: #8C8C8C !important;
    }
    input, textarea {
      color: #FFFFFF !important;
      -webkit-text-fill-color: #FFFFFF !important;
      caret-color: #FFFFFF;
    }
    input::placeholder, textarea::placeholder {
      color: #FFFFFF !important;
      -webkit-text-fill-color: #FFFFFF !important;
      opacity: 1;
    }
    /* SVG text elements (NHO Employee Profile charts, Victory charts in dark
       mode). Scoped here (dark mode only) so they do not make Victory labels
       invisible on the light background. Component-level fill overrides set
       directly on VictoryLabel/VictoryAxis take precedence over these rules
       because inline SVG presentation attributes and Victory's explicit style
       props outrank inherited CSS ; these rules exist purely as a
       fallback for SVG text elements that have no explicit fill of their own. */
    svg text, svg tspan {
      fill: #FFFFFF !important;
      color: #FFFFFF !important;
    }
    .legend-text,
    .metric-text,
    .chart-text {
      fill: #FFFFFF !important;
      color: #FFFFFF !important;
    }
    /* MapLibre map (Admin > Location Management / Dashboard). Marker labels are
       plain DOM nodes with an inline pale background and the attribution box
       uses MapLibre's own pale CSS; the rules above force their text white, so
       give both a dark surface instead of leaving white-on-white. */
    .lh-map-marker-label {
      background: rgba(32, 32, 32, 0.92) !important;
      border-color: rgba(255, 255, 255, 0.25) !important;
      color: #FFFFFF !important;
    }
    .maplibregl-ctrl-attrib,
    .maplibregl-ctrl-attrib.maplibregl-compact {
      background-color: rgba(32, 32, 32, 0.85) !important;
    }
    .maplibregl-ctrl-attrib a {
      color: #FFFFFF !important;
    }
    /* Dark mode scrollbar */
    ::-webkit-scrollbar-thumb {
      background: #555 !important;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: #777 !important;
    }
    ::-webkit-scrollbar-thumb:active {
      background: #999 !important;
    }
    * {
      scrollbar-color: #555 transparent !important;
    }
    *:hover {
      scrollbar-color: #777 transparent !important;
    }
  `;

  if (!existing) {
    document.head.appendChild(style);
  }
};

// ---------------------------------------------------------------------------
// Instant, event-driven catch-up for lazily-loaded screens.
//
// react-native-web inserts every StyleSheet.create() rule via
// `sheet.insertRule(cssText, position)` (confirmed in
// createOrderedCSSStyleSheet.js's `insertRuleAt`). Patching
// `CSSStyleSheet.prototype.insertRule` ONCE lets us react the INSTANT any
// new rule lands in RNW's shared stylesheet — including rules from a
// React.lazy screen chunk that only just finished loading — and sweep it
// immediately, before the browser paints. This eliminates the visible
// "flash" of un-swept Light colors that a polling interval alone would
// leave as a latency gap between paints.
// ---------------------------------------------------------------------------
let currentResolvedTheme: "light" | "dark" = "light";
let insertRulePatchApplied = false;

const patchInsertRuleForInstantSweep = (): void => {
  if (insertRulePatchApplied) return;
  if (typeof CSSStyleSheet === "undefined") return;
  insertRulePatchApplied = true;

  const original = CSSStyleSheet.prototype.insertRule;
  CSSStyleSheet.prototype.insertRule = function (
    this: CSSStyleSheet,
    ...args: Parameters<CSSStyleSheet["insertRule"]>
  ): number {
    const index = original.apply(this, args);
    const ownerNode = this.ownerNode as HTMLElement | null;
    if (
      currentResolvedTheme === "dark" &&
      ownerNode &&
      ownerNode.id === STYLESHEET_ID
    ) {
      sweepStylesheet("dark");
    }
    return index;
  };
};

export const applyGlobalDarkModeWeb = (
  resolvedTheme: "light" | "dark"
): void => {
  if (!isWeb()) return; // No-op on native; see module docblock.
  currentResolvedTheme = resolvedTheme;
  patchInsertRuleForInstantSweep();
  sweepStylesheet(resolvedTheme);
  applyRootBackground(resolvedTheme);
  applyRootTextDefaults(resolvedTheme);
};
